import { useMemo } from 'react';

import { useCapabilities } from '@/hooks/useCapabilities';
import { useTranslation } from '@/i18n';
import type { MenuItemId } from '@/models/channelModels';
import { PortalCustomizations } from '@/models/customizationModels';
import { selectPortalCustomizationsCustomization, selectSidebarCustomTabs } from '@/redux/customizations/reducers';
import { selectActiveTabsCustomization } from '@/redux/customizations/selectors';
import { useAppSelector } from '@/redux/hooks';

const defaultTabs: MenuItemId[] = [
  'dashboard',
  'stats',
  'referral_network',
  'rewards',
  'transactions',
  'activity',
  'assets',
  'sharing',
  'payment_settings',
  'profile',
];

const getDefaultIcons = (tab: MenuItemId) => {
  switch (tab) {
    case 'dashboard':
      return 'dashboard';
    case 'stats':
      return 'stats';
    case 'rewards':
      return 'loyalty';
    case 'activity':
      return 'payouts';
    case 'assets':
      return 'assets';
    case 'sharing':
      return 'sharelinks';
    case 'profile':
      return 'profile';
    case 'transactions':
      return 'coin-dollar';
    case 'referral_network':
      return 'fa-circle-nodes';
    case 'payment_settings':
      return 'fa-building-columns';
    default:
      return 'dashboard';
  }
};

const getDefaultLinks = (tab: string, hasV3Access?: boolean, selectedCampaignId?: number) => {
  switch (tab) {
    case 'dashboard':
      return selectedCampaignId ? `/dashboard/${selectedCampaignId}` : '/dashboard';
    case 'stats':
      return '/app/stats';
    case 'rewards':
      return hasV3Access ? '/app/rewards/v3' : '/app/rewards';
    case 'transactions':
      return '/app/transactions';
    case 'activity':
      return '/app/activity-portal';
    case 'assets':
      return '/app/assets';
    case 'sharing':
      return '/app/sharing';
    case 'profile':
      return '/app/account';
    case 'referral_network':
      return '/app/referral-network';
    case 'payment_settings':
      return '/app/payments/ach-direct-deposit';
    default:
      return '/app';
  }
};

const getTextKey = (tab: MenuItemId) => `${tab}_tab_text` as keyof PortalCustomizations['tabs'];

const getShowKey = (tab: MenuItemId) => {
  switch (tab) {
    case 'stats':
    case 'activity':
    case 'assets':
      return `show_${tab}_tab` as keyof PortalCustomizations['tabs'];
    default:
      return `show_${tab}` as keyof PortalCustomizations['tabs'];
  }
};

export type MenuItemType = {
  icon: string;
  id: MenuItemId | string;
  link: string;
  name: string;
  useSvg?: boolean;
};

export default function useMenuItems(): MenuItemType[] {
  const { t } = useTranslation('dashboard');
  const tabs = useAppSelector(selectActiveTabsCustomization) ?? ({} as any);
  const companyCustomTabs = useAppSelector(selectSidebarCustomTabs);
  const company = useAppSelector((state) => state.auth.companyAccount);
  const selectedCampaignId = useAppSelector((state) => state.campaigns.selectedCampaignId);
  const hasPortal3Access = company?.portal_v3_access;
  const caps = useCapabilities();

  return useMemo(() => {
    let fixedTabs = defaultTabs
      // filter by feature gate
      .filter((tab) => !caps.allowedMenuItems || caps.allowedMenuItems.has(tab))
      // admin3 & activity rules
      .filter((tab) => {
        if (tab === 'transactions' && !hasPortal3Access) return false;
        if (tab === 'activity' && hasPortal3Access) return false;
        if (tab === 'referral_network') {
          return Boolean(hasPortal3Access) && tabs.show_referral_network_tab === true;
        }
        if (tab === 'payment_settings') return Boolean(company?.stripe_rewards_access);
        return true;
      })
      // map to menu items
      .map((tab) => ({
        icon: getDefaultIcons(tab),
        id: tab,
        link: getDefaultLinks(tab, hasPortal3Access, selectedCampaignId),
        name: (tabs[getTextKey(tab)] as string) || t(`menuitems.${tab}`),
      }))
      // respect portal customization visibility
      .filter((item) => tabs[getShowKey(item.id as MenuItemId)] !== false);

    // 2) Custom tabs allowed?
    const customTabs =
      caps.allowCustomSidebarTabs !== false
        ? companyCustomTabs.map((tab) => ({
            icon: tab.icon,
            id: tab.slug,
            link: `/${tab.slug}`,
            name: tab.name,
            useSvg: true,
          }))
        : [];

    // 3) Order per customization
    const ordered = [...fixedTabs, ...customTabs].sort((a, b) => {
      const aIndex = tabs.order?.indexOf(a.id as string) ?? -1;
      const bIndex = tabs.order?.indexOf(b.id as string) ?? -1;
      return aIndex - bIndex;
    });

    return ordered;
  }, [caps.allowedMenuItems, caps.allowCustomSidebarTabs, companyCustomTabs, company, hasPortal3Access, selectedCampaignId, t, tabs]);
}
